import {useContext, useState} from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import {CategorieWeb} from 'api-types/ffconnect';
import {Link, useNavigate} from 'react-router-dom';
import {routes, useLivraisonsPossiblesContext} from 'ff-common';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  StarIcon,
} from '@chakra-ui/icons';
import {Searchbar} from '@/components/Navbar/Searchbar/Searchbar';
import {CategorieImage} from './CategorieImage';
import {useTranslation} from 'react-i18next';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {PromotionIcon} from '@/components/svg/PromotionIcon';
import {ElapsedTime} from '@orama/orama';
import {OperationCommercialeListeView} from '@/domains/operation_commerciale/OperationCommercialeListeView.tsx';
import {useClient} from '@/haligator-factory.tsx';
import {useHalCollection} from '@synako/haligator';
import {BreakPointContext} from '@/chakra/BreakpointProvider';
import {FiBookOpen} from 'react-icons/fi';

interface PromoCounts {
  count: number;
  values: Record<string, number>;
  totalCount: number;
  elapsed: ElapsedTime;
}

interface CategoriesViewProps {
  categories: CategorieWeb[] | undefined;
  onClose: () => void;
  counts: Record<string, number>;
  promoCounts: PromoCounts | undefined;
}

const DRAWER_WIDTH = 500; // largeur d'un volet en desktop

export const CategoriesView: React.FC<CategoriesViewProps> = ({
  categories,
  onClose,
  counts,
  promoCounts,
}) => {
  // Chaque fois qu'un utilisateur sélectionne une catégorie, elle est ajoutée à selectedCategories
  const [selectedCategories, setSelectedCategories] = useState<CategorieWeb[]>(
    [],
  );
  const [suggestion_selected, setSuggestionSelection] = useState(false);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {buildClientRoute} = useClientRoute();
  const totalPromoCount = promoCounts?.totalCount;
  const breakpointContext = useContext(BreakPointContext);
  const isMobile = breakpointContext?.isMobile ?? false;

  const {effectiveTemperature} = useLivraisonsPossiblesContext();

  const temperature_livraison_param =
    effectiveTemperature === 'les_deux' ? undefined : effectiveTemperature;

  const operation_commerciale_nav = useClient().follow(
    'operations_commerciales_eligible',
  );
  const operation_commerciale_data = useHalCollection(
    operation_commerciale_nav,
  );
  const hasOperationCommerciale =
    operation_commerciale_data.items &&
    operation_commerciale_data.items.length > 0;

  const marketplacesNav = useClient().follow('marketplaces');
  const marketplacesData = useHalCollection(marketplacesNav);
  const hasMarketplaces =
    marketplacesData.items && marketplacesData.items.length > 0;

  // vérification si une catégorie a des promotions
  const hasPromotions = (categoryId: string) => {
    return (
      promoCounts?.values[categoryId] && promoCounts.values[categoryId] > 0
    );
  };

  // permet de renvoyer vers le catalogue pour le dernier niveau ou d'ajouter à la liste des categories
  const handleCategorieSelect = (category: CategorieWeb, level: number) => {
    // verification si la catégorie sélectionnée a des sous-catégories
    const hasSubcategories = categories?.some(
      (cat) => cat.id_parent === category.id,
    );

    // si pas de sous categoriees alors envoie vers le catalogue
    if (!hasSubcategories) {
      const catalogueUrl = buildClientRoute(
        routes.client.catalogue,
        {},
        {
          cat_web: category.id,
          temperature_livraison: temperature_livraison_param,
        },
      );
      navigate(catalogueUrl);
      onClose();
      // si la catégorie a des sous-catégories, selectedCategories mis à jour
      // prev.slice(0, level) conserve toutes les catégories sélectionnées jusqu'au niveau actuel
      // nouvelle catégorie ajoutée à la fin de cette liste
    } else {
      setSuggestionSelection(false);
      setSelectedCategories((prev) => [...prev.slice(0, level), category]);
    }
  };

  const handleSuggestionSelect = () => {
    setSuggestionSelection(true);
    setSelectedCategories([]);
  };

  // affiche chaque niveau de catégorie, en tenant compte de la hiérarchie
  const renderCategorieLevel = (level: number) => {
    // level === 0 (premier niveau), pas de catégorie parente || catégorie parente est la catégorie sélectionnée au niveau précédent.
    const parentCategory =
      level === 0 ? undefined : selectedCategories[level - 1];
    const filteredCategories = parentCategory
      ? categories?.filter(
          (cat) =>
            cat.id_parent === parentCategory.id &&
            cat.id !== parentCategory.id &&
            counts[cat.id] > 0,
        )
      : // pas de parent, selection des catégories de premier niveau. Les catégories de premier niveau ont un id_parent vide
        categories?.filter(
          (cat) =>
            cat.id_parent === '' &&
            counts[cat.id] > 0 &&
            cat.libelle !== 'Divers',
        );

    // creation du breadcrumb pour mobile
    const handleBreadcrumbClick = (index: number) => {
      setSelectedCategories((prev) => prev.slice(0, index + 1));
    };

    const hasSubcategories = (categoryId: string) => {
      return categories?.some((cat) => cat.id_parent === categoryId);
    };

    const handlePromoClick = () => {
      navigate(
        buildClientRoute(
          routes.client.catalogue,
          {},
          {
            nature_du_prix: 'promo',
            from: 'promo',
            temperature_livraison: temperature_livraison_param,
          },
        ),
      );
      onClose();
    };

    const suggestion_button = isMobile ? (
      <>
        <OperationCommercialeListeView
          context='search_bar'
          onRedirect={onClose}
        />
      </>
    ) : (
      <Grid
        templateColumns={'80px 1fr auto'}
        gap={4}
        alignItems='center'
        bg={suggestion_selected ? 'gray.bg' : undefined}
        _hover={{bg: 'gray.100'}}
        pt={2}
        pb={2}
        pl='40px'
        borderTop={{base: 'solid 1px', md: 'none'}}
        borderColor={{base: 'gray.light', md: 'none'}}
        height='70px'
        cursor='pointer'
        onClick={handleSuggestionSelect}
      >
        <GridItem
          display='flex'
          w='70px'
          justifyContent='center'
          color='blue.600'
        >
          <StarIcon />
        </GridItem>
        <GridItem
          justifyContent='space-between'
          w='100%'
          color='blue.main'
        >
          <Text fontWeight='600'>{t('menu_catalogue.suggestion_produit')}</Text>
        </GridItem>
        <GridItem pr='20px'>
          <ChevronRightIcon boxSize='20px' />
        </GridItem>
      </Grid>
    );

    return (
      <Box
        key={level}
        w={
          suggestion_selected && level === 1
            ? '100%'
            : {base: '100vw', md: `${DRAWER_WIDTH}px`}
        }
        minW={DRAWER_WIDTH}
        h='100%'
        overflowY='auto'
        bg='white'
        pt={{base: '0', md: '30px'}}
        zIndex={101}
      >
        {/* Uniquement en mobile */}
        {level > 0 && !suggestion_selected && (
          <HStack
            display={{base: 'flex', md: 'none'}}
            alignItems='center'
            justifyContent='space-between'
            mb={4}
            p='10px'
            pl='25px'
            w='100%'
            h='90px'
            bg='categories.produits_laitiers'
          >
            <HStack spacing={1}>
              {selectedCategories.slice(0, level).map((cat, index) => (
                <HStack key={cat.id}>
                  {index > 0 && <Text color='gray.500'>{'>'}</Text>}
                  {/* obtention du breadcrumb */}
                  <Text
                    fontSize={index === level - 1 ? 'lg' : 'md'}
                    fontWeight={index === level - 1 ? 'bold' : '400'}
                    color={index === level - 1 ? 'black' : 'gray.500'}
                    cursor='pointer'
                    onClick={() => {
                      handleBreadcrumbClick(index);
                    }}
                  >
                    {cat.libelle}
                  </Text>
                </HStack>
              ))}
            </HStack>
            <CategorieImage
              categorieId={selectedCategories[0].id}
              libelle={selectedCategories[0].libelle}
              objectFit='cover'
              height='50px'
            />
          </HStack>
        )}
        <VStack
          align='stretch'
          spacing={0}
          pt={{base: 0, md: 4}}
          mb={'80px'}
        >
          {level === 0 && totalPromoCount && totalPromoCount > 0 && (
            <Grid
              templateColumns='80px 1fr auto'
              gap={4}
              alignItems='center'
              _hover={{bg: 'gray.100'}}
              pt={2}
              pb={2}
              pl='40px'
              borderTop={{base: 'solid 1px', md: 'none'}}
              borderColor={{base: 'gray.light', md: 'none'}}
              height='70px'
              cursor='pointer'
              onClick={handlePromoClick}
            >
              <GridItem
                display='flex'
                w='70px'
                justifyContent='center'
              >
                <PromotionIcon
                  color='red.200'
                  height='24px'
                  width='24px'
                />
              </GridItem>
              <GridItem
                justifyContent='space-between'
                w='100%'
              >
                <Text
                  color='red.200'
                  fontWeight={500}
                >
                  {t('catalogue.promotion_du_moment')}
                </Text>
              </GridItem>
              <GridItem pr='20px'>
                {/* Espace pour alignement avec les autres éléments */}
              </GridItem>
            </Grid>
          )}
          {level === 1 &&
            selectedCategories[0] &&
            hasPromotions(selectedCategories[0].id) && (
              <Link
                to={buildClientRoute(
                  routes.client.catalogue,
                  {},
                  {
                    cat_web: selectedCategories[0].id,
                    nature_du_prix: 'promo',
                    temperature_livraison: temperature_livraison_param,
                  },
                )}
                style={{width: '100%'}}
              >
                <Grid
                  templateColumns='1fr auto'
                  gap={4}
                  alignItems='center'
                  _hover={{bg: 'gray.100'}}
                  pt={2}
                  pb={2}
                  pl='40px'
                  borderTop={{base: 'solid 1px', md: 'none'}}
                  borderColor={{base: 'gray.light', md: 'none'}}
                  height='70px'
                  cursor='pointer'
                  onClick={onClose}
                >
                  <GridItem
                    justifyContent='space-between'
                    w='100%'
                    display='flex'
                    alignItems='center'
                    gap={3}
                  >
                    <PromotionIcon
                      color='red.200'
                      height='24px'
                      width='24px'
                    />
                    <Text
                      color='red.200'
                      fontWeight={500}
                    >
                      {t('catalogue.promotion_du_moment')}
                    </Text>
                  </GridItem>
                  <GridItem pr='20px'>
                    {/* Espace pour alignement avec les autres éléments */}
                  </GridItem>
                </Grid>
              </Link>
            )}
          {level > 0 && !suggestion_selected && (
            <Link
              to={buildClientRoute(
                routes.client.catalogue,
                {},
                {
                  cat_web: selectedCategories[level - 1].id,
                  temperature_livraison: temperature_livraison_param,
                },
              )}
            >
              <HStack
                p={2}
                pl='40px'
                bg='white'
                h='70px'
                w='100%'
                _hover={{bg: 'gray.100'}}
                cursor='pointer'
                onClick={() => {
                  onClose();
                }}
              >
                <Text
                  mr='auto'
                  color='blue.main'
                  fontWeight={500}
                >
                  {t('catalogue.voir_tout')}
                </Text>
              </HStack>
            </Link>
          )}
          {level === 0 && hasOperationCommerciale && suggestion_button}
          {level === 0 &&
            hasMarketplaces &&
            marketplacesData.items?.map((item, index) => {
              const marketplace = item.data;
              return (
                <Grid
                  key={index}
                  templateColumns='80px 1fr auto'
                  gap={4}
                  alignItems='center'
                  _hover={{bg: 'gray.100'}}
                  pt={2}
                  pb={2}
                  pl='40px'
                  borderTop={{base: 'solid 1px', md: 'none'}}
                  borderColor={{base: 'gray.light', md: 'none'}}
                  height='70px'
                  cursor='pointer'
                  onClick={() => {
                    const catalogueUrl = buildClientRoute(
                      routes.client.catalogue,
                      {},
                      {marketplace_id: marketplace.marketplace_id},
                    );
                    navigate(catalogueUrl);
                    onClose();
                  }}
                >
                  <GridItem
                    display='flex'
                    w='70px'
                    justifyContent='center'
                  >
                    {marketplace.logo_vertical_couleur ? (
                      <Image
                        src={`${import.meta.env['VITE_MEDIA_URL']}${marketplace.logo_vertical_couleur}`}
                        alt={marketplace.nom}
                        h='40px'
                        objectFit='contain'
                      />
                    ) : (
                      <Icon
                        as={FiBookOpen}
                        boxSize='24px'
                        color='gray.dark'
                      />
                    )}
                  </GridItem>
                  <GridItem w='100%'>
                    <Text
                      fontWeight='600'
                      color='gray.dark'
                    >
                      {t(
                        `menu_catalogue.catalogue_${marketplace.marketplace_id}`,
                      )}
                    </Text>
                    <Text
                      fontSize='sm'
                      color='gray.500'
                    >
                      {marketplace.description}
                    </Text>
                  </GridItem>
                  <GridItem pr='20px'>
                    <ChevronRightIcon boxSize='20px' />
                  </GridItem>
                </Grid>
              );
            })}
          {suggestion_selected && level > 0 && (
            <OperationCommercialeListeView onRedirect={onClose} />
          )}
          {filteredCategories?.map((category: CategorieWeb) => (
            <Grid
              key={category.id}
              templateColumns={level === 0 ? '80px 1fr auto' : '1fr auto'}
              gap={4}
              alignItems='center'
              bg={
                selectedCategories[level]?.id === category.id
                  ? 'gray.bg'
                  : 'transparent'
              }
              _hover={{bg: 'gray.100'}}
              pt={2}
              pb={2}
              pl='40px'
              borderTop={{base: 'solid 1px', md: 'none'}}
              borderColor={{base: 'gray.light', md: 'none'}}
              height='70px'
              cursor='pointer'
              onClick={() => {
                handleCategorieSelect(category, level);
              }}
            >
              {/* affichage des images pour les catégories de premier niveau */}
              {level === 0 && (
                <GridItem w='70px'>
                  <CategorieImage
                    categorieId={category.id || ''}
                    libelle={category.libelle || ''}
                    objectFit='cover'
                  />
                </GridItem>
              )}
              <GridItem
                justifyContent='space-between'
                w='100%'
              >
                <Text
                  fontWeight={level === 0 ? '600' : '500'}
                  color='gray.dark'
                  _hover={{color: 'blue.main'}}
                >
                  {category.libelle}
                </Text>
              </GridItem>
              {hasSubcategories(category.id) && (
                <GridItem pr='20px'>
                  <ChevronRightIcon boxSize='20px' />
                </GridItem>
              )}
            </Grid>
          ))}
        </VStack>
      </Box>
    );
  };

  const renderSuggestionLevel = () => {
    return (
      <Box
        // width="100%"
        // Tout le reste de l'écran : les bandeaux n'ont pas de largeur propre, ils prennent celle du volet.
        w={`calc(100vw - ${DRAWER_WIDTH}px)`}
        // 640 px de bandeau et 24 px de marge de chaque côté : la plus petite largeur où un bandeau
        // avec date et description garde son ratio, son texte l'allongeant en dessous.
        maxW='688px'
        h='100%'
        overflowY='auto'
        bg='white'
        zIndex={101}
      >
        <OperationCommercialeListeView
          context='menu_catalogue'
          onRedirect={onClose}
        />
      </Box>
    );
  };

  return (
    <Box
      height='100%'
      // w={suggestion_selected ? '100%' : undefined}
      overflowX='hidden'
    >
      {/* Version mobile */}
      <Box
        height='100%'
        display={{base: 'block', md: 'none'}}
      >
        <Box
          position='sticky'
          top={0}
          zIndex={10}
          bg='white'
        >
          <HStack
            spacing={4}
            alignItems='center'
            bg='gray.bg'
            p={4}
          >
            <Icon
              as={selectedCategories.length > 0 ? ChevronLeftIcon : CloseIcon}
              boxSize={selectedCategories.length > 0 ? 6 : 3}
              mr={selectedCategories.length > 0 ? '0' : '12px'}
              cursor='pointer'
              onClick={
                selectedCategories.length > 0
                  ? () => {
                      setSelectedCategories((prev) => prev.slice(0, -1));
                    }
                  : onClose
              }
            />
            <Box flex={1}>
              <Searchbar props={{bg: 'white'}} />
            </Box>
          </HStack>
        </Box>
        {/* affichage des catégories correspondant au niveau actuel de profondeur dans la hiérarchie */}
        {renderCategorieLevel(selectedCategories.length)}
      </Box>

      {/* Version desktop */}
      <Flex
        height='100%'
        w={suggestion_selected ? '100%' : undefined}
        overflowX='auto'
        display={{base: 'none', md: 'flex'}}
      >
        {/* affiche toujours le premier niveau des catégories*/}
        {renderCategorieLevel(0)}
        {/* affiche les catégories de niveau inférieur*/}
        {suggestion_selected && renderSuggestionLevel()}
        {selectedCategories.map((_, index) => renderCategorieLevel(index + 1))}
      </Flex>
    </Box>
  );
};
